import { client, gql } from 'data-provider';
import { IManufacture, IProductManufacture } from 'interfaces';

interface FindRecipeInventoryStatusInput {
  data: {
    variantId: string;
    variantQuantity: number;
    warehouseId: string;
  };
}

interface UsedProductRecipe {
  inventoryQuantity: number;
  availability: string;
  note: string;
  usedProductCost: number;
  usedProductQuantity: number;
  usedVariantId: string;
  usedVariantName: string;
  usedVariantSku: string;
}

interface FindRecipeWithStatusResponse {
  madeUpVariantName: string;
  madeUpVariantSku: string;
  purchasePrice: number;
  sellingPrice: number;
  usedProductRecipes: UsedProductRecipe[];
  madeUpVariantId: string;
}

interface ProductWithRecipesListResponse {
  variantId: string;
  variantName: string;
}

export interface IManufactureQuery {
  startDate?: string;
  endDate?: string;
  searchString?: string;
  start?: number;
  limit?: number;
}

// Query to fetch product with recipes list
const PRODUCT_WITH_RECIPES_LIST_QUERY = gql`
  query ProductWithRecipesList {
    productWithRecipesList {
      variantId
      variantName
    }
  }
`;

export const getProductWithRecipesList = async (): Promise<ProductWithRecipesListResponse[]> => {
  try {
    const data = await client.request<{ productWithRecipesList: ProductWithRecipesListResponse[] }>(
      PRODUCT_WITH_RECIPES_LIST_QUERY
    );
    return data.productWithRecipesList;
  } catch (error) {
    console.error('Failed to fetch product with recipes list:', error);
    throw error;
  }
};

// Query to find recipe with status
const FIND_RECIPE_WITH_STATUS_QUERY = gql`
  query FindRecipeWithStatus($input: findRecipeInventoryStatusInput!) {
    findRecipeWithStatus(input: $input) {
      madeUpVariantName
      madeUpVariantSku
      purchasePrice
      sellingPrice
      usedProductRecipes {
        inventoryQuantity
        availability
        note
        usedProductCost
        usedProductQuantity
        usedVariantId
        usedVariantName
        usedVariantSku
        usedProductUnitPrice
      }
      madeUpVariantId
    }
  }
`;

export const findRecipeWithStatus = async (
  input: FindRecipeInventoryStatusInput
): Promise<FindRecipeWithStatusResponse> => {
  try {
    const data = await client.request<{ findRecipeWithStatus: FindRecipeWithStatusResponse }>(
      FIND_RECIPE_WITH_STATUS_QUERY,
      { input }
    );
    return data.findRecipeWithStatus;
  } catch (error) {
    console.error('Failed to find recipe with status:', error);
    throw error;
  }
};

export interface CreateManufactureInput {
  input: {
    data: {
      transactionToken: string;
      warehouse: string;
      variantId: string;
      quantity: number;
      productionDeadlineDate: string;
      manufactureStatus: string;
      manufactureProducts: {
        materialSku: string;
        quantity: number;
        unitPrice: number;
      }[];
    };
  };
}

export const createManufacture = async (data: any) => {
  const query = gql`
    mutation CreateManufacture($input: createManufactureInput!) {
      createManufacture(input: $input) {
        id
      }
    }
  `;

  const variables = { input: { data } };
  const createdData = await client.request(query, variables);
  return createdData.createManufacture as { id: string };
};

export const getManufactures = async (manufactureQuery?: IManufactureQuery) => {
  const where = {
    query: manufactureQuery?.searchString ?? '',
    startDate: manufactureQuery?.startDate ?? undefined,
    endDate: manufactureQuery?.endDate ?? undefined,
  };

  const query = gql`
    query ($sort: SortInput, $where: JSON, $start: Int, $limit: Int) {
      manufactures(sort: $sort, where: $where, start: $start, limit: $limit) {
        data {
          id
          productVariant {
            name
            productCategory {
              name
            }
          }
          quantity
          productionDeadlineDate
          manufactureStatus
          warehouse {
            name
          }
          manufactureExpense
          manufactureWastage
          totalAmount
          manufactureProducts {
            quantity
            unitPrice
          }
        }
        total
        size
        page
      }
    }
  `;

  const variables = {
    where,
    start: manufactureQuery?.start ?? 1,
    limit: manufactureQuery?.limit ?? 10,
  };

  const data = await client.request(query, variables);
  return data.manufactures as {
    data: IManufacture[];
    page: number;
    size: number;
    total: number;
  };
};

export const getManufacture = async (manufactureId: string) => {
  const query = `
    query Manufacture($manufactureId: ID!) {
      manufacture(id: $manufactureId) {
        batchInfo
        id
        manufactureExpense
        manufactureStatus
        manufactureWastage
        note
        productionDeadlineDate
        quantity
        warehouse {
          id
          name
        }
        productVariant {
          id
          name
        }
      }
    }
  `;

  const variables = { manufactureId };
  const data = await client.request(query, variables);
  return data.manufacture as IProductManufacture;
};

export const updateProductManufacture = async (id: string, data: any) => {
  const query = gql`
    mutation UpdateManufactureStatus($input: updateManufactureStatusInput) {
      updateManufactureStatus(input: $input) {
        manufacture {
          id
        }
      }
    }
  `;

  const variables = { input: { where: { id }, data } };
  const updateManufactureData = await client.request(query, variables);
  return updateManufactureData.updateManufactureStatus as { manufacture: { id: string } };
};

export const deleteManufactureProduct = async (id: string) => {
  const mutation = gql`
    mutation DeleteManufacture($input: deleteManufactureInput!) {
      deleteManufacture(input: $input) {
        manufacture {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      where: { id },
    },
  };
  const response = await client.request(mutation, variables);
  return response.deleteManufacture as { manufacture: { id: string } };
};
